import { useCallback, useEffect, useMemo, useState } from 'react';
import { Select, Table, Tag } from 'antd';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import ButtonPrimaryMedium from '../../../components/common/ButtonPrimary/ButtonPrimaryMedium';
import ButtonIconGhostSmall from '../../../components/common/ButtonIconGhost/ButtonIconGhostSmall';
import Tooltip from '../../../components/common/Tooltip/Tooltip';
import SearchComponent from '../../../components/common/SearchComponent/SearchComponent';
import { Icon } from '../../../components/icon/icon';
import { openMessageError } from '../../../components/common/Message/openMessageError';
import { PERMISSION, PERMISSION_FLAG } from '../../../constants/permissions';
import { can } from '../../../utils/permissions';
import { fetchCustomers } from '../../../services/customers/customersApi';
import { RenderPagination } from '../CustomersPage/NewCustomerPage';
import CustomerFormModal from './CustomerFormModal';
import CustomerDetailDrawer from './CustomerDetailDrawer';

const DEFAULT_LIMIT = 20;

// Khách ảo do nhân viên tạo để gắn vào thiết kế và chốt giá. Khác trang "Khách hàng" (tài khoản người dùng).
const DesignCustomersPage = () => {
  const { t } = useTranslation();
  const { user } = useSelector(state => state.auth);
  const canCreate = can(user, PERMISSION.CUSTOMERS, PERMISSION_FLAG.CREATE);
  const canEdit = can(user, PERMISSION.CUSTOMERS, PERMISSION_FLAG.EDIT);

  const [searchParams, setSearchParams] = useSearchParams();
  const search = searchParams.get('search') ?? '';
  const confirmedParam = searchParams.get('confirmed') ?? 'all';
  const offset = Number(searchParams.get('offset') ?? 0);
  const limit = Number(searchParams.get('limit') ?? DEFAULT_LIMIT);

  const [data, setData] = useState({ customers: [], total: 0 });
  const [isLoading, setIsLoading] = useState(false);
  const [formState, setFormState] = useState({ open: false, customer: null });
  const [detailId, setDetailId] = useState(null);

  const updateParams = useCallback(
    (key, value) => {
      const params = Object.fromEntries(searchParams);
      if (value === '' || value == null || value === 'all') delete params[key];
      else params[key] = value;
      if (key !== 'offset') delete params.offset;
      setSearchParams(params);
    },
    [searchParams, setSearchParams]
  );

  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      setData(
        await fetchCustomers({
          search: search || undefined,
          isConfirmed:
            confirmedParam === 'all' ? undefined : confirmedParam === 'true',
          limit,
          offset,
        })
      );
    } catch {
      openMessageError({ content: t('Unable to load customers') });
    } finally {
      setIsLoading(false);
    }
  }, [search, confirmedParam, limit, offset, t]);

  useEffect(() => {
    load();
  }, [load]);

  // Drawer đọc khách từ danh sách để luôn hiển thị trạng thái chốt mới nhất sau khi tải lại
  const detailCustomer = useMemo(
    () => data.customers.find(item => item.id === detailId) ?? null,
    [data.customers, detailId]
  );

  const columns = [
    { title: t('ID'), dataIndex: 'id', key: 'id', width: 250 },
    { title: t("Customer's name"), dataIndex: 'name', key: 'name', width: 250 },
    { title: t('Email'), dataIndex: 'email', key: 'email', width: 300 },
    { title: t('Phone number'), dataIndex: 'phone', key: 'phone', width: 180 },
    {
      title: t('Status'),
      dataIndex: 'is_confirmed_contract',
      key: 'status',
      width: 140,
      render: confirmed => (
        <Tag color={confirmed ? 'green' : 'default'}>
          {confirmed ? t('Confirmed') : t('Not confirmed')}
        </Tag>
      ),
    },
    {
      title: t('Actions'),
      key: 'actions',
      width: 240,
      render: (_, record) => (
        <div className="flex items-center justify-start">
          <ButtonIconGhostSmall
            icon={<Icon.ICVisibility />}
            onClick={() => setDetailId(record.id)}
          />
          {canEdit && (
            <ButtonIconGhostSmall
              icon={<Icon.ICEdit />}
              onClick={() => setFormState({ open: true, customer: record })}
            />
          )}
        </div>
      ),
    },
  ].map(column => ({
    ...column,
    // ô dữ liệu cùng kiểu trang Tài khoản
    render:
      column.render ??
      (value => (
        <div className="flex px-12 py-8 items-center justify-start">
          <span className="text-body-medium">{value}</span>
        </div>
      )),
  }));

  return (
    <div className="flex p-16 items-start gap-16 w-full h-full flex-col">
      <div className="flex justify-between items-center self-stretch">
        <h2 className="text-title-medium-emphasized">{t('Customers')}</h2>
        {canCreate && (
          <ButtonPrimaryMedium
            title={t('Create customer')}
            posIcon={<Icon.ICAddCircle />}
            onClick={() => setFormState({ open: true, customer: null })}
          />
        )}
      </div>
      <section className="flex flex-col items-start grow self-stretch rounded-[16px] bg-white">
        <div className="flex p-16 pb-8 items-center gap-12 self-stretch">
          <SearchComponent
            placeholder={t('Search name, phone, email...')}
            className="w-[250px]"
            setValue={value => updateParams('search', value)}
          />
          <Select
            value={confirmedParam}
            onChange={value => updateParams('confirmed', value)}
            style={{ width: 180 }}
            options={[
              { value: 'all', label: t('All statuses') },
              { value: 'true', label: t('Confirmed') },
              { value: 'false', label: t('Not confirmed') },
            ]}
          />
        </div>
        <div className="flex px-16 py-8 flex-col items-start grow self-stretch">
          <Table
            rowKey="id"
            pagination={false}
            columns={columns}
            dataSource={data.customers}
            loading={isLoading}
            scroll={{ y: 600 }}
            components={{
              body: {
                row: props => (
                  <Tooltip
                    destroyOnHidden={true}
                    title={t('Double click to view details')}
                  >
                    <tr {...props}>{props?.children}</tr>
                  </Tooltip>
                ),
              },
            }}
            onRow={record => ({ onDoubleClick: () => setDetailId(record.id) })}
          />
        </div>

        <div className="flex justify-between items-center self-stretch rounded-[8px] left-0 right-0 bottom-0 p-16 pt-8 bg-white">
          <RenderPagination
            total={data.total}
            offsetParam={offset}
            limitParam={limit}
            updateSearchParams={updateParams}
          />
        </div>
      </section>

      <CustomerFormModal
        open={formState.open}
        customer={formState.customer}
        onClose={() => setFormState({ open: false, customer: null })}
        onSaved={load}
      />
      <CustomerDetailDrawer
        open={!!detailId}
        customer={detailCustomer}
        onClose={() => setDetailId(null)}
        onChanged={load}
      />
    </div>
  );
};

export default DesignCustomersPage;
